import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import { TextLink } from "@itandi/itandi-bb-ui/TextLink";
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { usePropertySelectHandler } from "~/hooks/deals/useRoomBlockPropertySelect";
import { useDebounce } from "~/hooks/useDebounce";
import { usePropertyBuildingOptions } from "~/hooks/usePropertyBuildingOptions";
import { usePropertyRoomOptions } from "~/hooks/usePropertyRoomOptions";
import type { Deal } from "~/models/Deal";
import type { DealParam, DealUpdateParam } from "~/models/DealForm";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import type { PropertyBuildingOption } from "~/models/PropertyBuildingOption";
import {
	buildingOptions,
	buildPropertyBuildingOptionsFromRoomName,
	getLandExclusiveRoomDetailUrl,
	propertyBuildingOptionValue
} from "~/models/PropertyBuildingOption";
import type { PropertyRoomOption } from "~/models/PropertyRoomOption";
import {
	buildPropertyRoomOptionsFromRoomNumber,
	propertyRoomOptionValue,
	roomOptions
} from "~/models/PropertyRoomOption";
import {
	editRentParkingSpaceUrl,
	editRentRoomBuildingUrl,
	editRentRoomUrl
} from "~/utils/BukkenKanriPath";
import { FormRow } from "./FormRow";

type Props = { deal: Deal };

export function RoomBlockPropertySelectV2({ deal }: Props) {
	const { notify } = useNotification();
	const { company } = useContext(ItandiAccountContext);
	// KT経由で作成された案件はBKK以外の物件も保存されていることがあるため、この変数はfalseにする
	// trueのままだと、部屋番号の選択肢が返ってこず(BE側でBKK物件しか選択肢として返さなくなるため)、「棟または戸建てとして登録」に自動チェックが入ってしまう
	const showOnlyAmaterasProperties =
		!!company?.onlyAmaterasProperties && !deal.leasingId;
	const {
		register,
		control,
		formState: { errors },
		setValue
	} = useFormContext<DealParam | DealUpdateParam>();
	const isRegisterAsBuilding = useWatch({
		control,
		name: "isRegisterAsBuilding"
	});
	const debounce = useDebounce(200);
	const [inputRoomName, setInputRoomName] = useState<string>("");
	const [inputRoomNumber, setInputRoomNumber] = useState<string>("");
	const [selectedBuilding, setSelectedBuilding] =
		React.useState<PropertyBuildingOption | null>(
			deal.propertyBuildingOption ?? null
		);
	const [selectedRoom, setSelectedRoom] =
		React.useState<PropertyRoomOption | null>(deal.propertyRoomOption ?? null);
	const handleInputChangeBuilding = useCallback(
		(text: string) => {
			debounce(() => {
				setInputRoomName(text.trim());
			});
		},
		[debounce, setInputRoomName]
	);
	const handleInputChangeRoom = useCallback(
		(text: string) => {
			setInputRoomNumber(text.trim());
		},
		[setInputRoomNumber]
	);
	// フォームに表示する物件名と部屋番号の候補を取得
	const {
		data: propertyBuildingOptionsFromApi,
		isValidating: isPropertyBuildingOptionsValidating
	} = usePropertyBuildingOptions().index(inputRoomName);
	const {
		data: propertyRoomOptionsFromApi,
		isValidating: isPropertyRoomOptionsValidating
	} = usePropertyRoomOptions().index(
		selectedBuilding?.name ?? null,
		selectedBuilding?.address ?? null,
		selectedBuilding?.amaterasPropertyId ?? null
	);

	const propertyBuildingOptions = useMemo(() => {
		const roomNameOptions = showOnlyAmaterasProperties
			? []
			: buildPropertyBuildingOptionsFromRoomName(inputRoomName);

		return [...roomNameOptions, ...(propertyBuildingOptionsFromApi ?? [])];
	}, [
		showOnlyAmaterasProperties,
		inputRoomName,
		propertyBuildingOptionsFromApi
	]);
	const propertyRoomOptions = useMemo(() => {
		const roomNumberOptions = showOnlyAmaterasProperties
			? []
			: buildPropertyRoomOptionsFromRoomNumber(inputRoomNumber);

		return [...roomNumberOptions, ...(propertyRoomOptionsFromApi ?? [])];
	}, [showOnlyAmaterasProperties, inputRoomNumber, propertyRoomOptionsFromApi]);
	// BKKのみフラグがONで部屋が登録されていないBKK物件を選択した時、自動で棟または戸建てとして登録にチェックを入れる
	const shouldDisableRegisterAsBuilding =
		showOnlyAmaterasProperties &&
		!!selectedBuilding &&
		!isPropertyRoomOptionsValidating &&
		propertyRoomOptions.length === 0;
	const shouldDisableAddress =
		!!selectedBuilding?.amaterasPropertyId && !!selectedBuilding.address;
	const landExclusiveDetailUrl =
		getLandExclusiveRoomDetailUrl(selectedBuilding);
	// KTで駐車場区分が紐づいている契約でNSKの案件作成に訪れたとき、BKK駐車場区画へのリンクを出す対応
	const showParkingSpaceProductLink = deal.propertyType === "parking_space";

	const {
		onChangeBuilding,
		onChangeRoom,
		onChangeRegisterAsBuildingCheck,
		onBlurBuilding,
		onBlurRoom
	} = usePropertySelectHandler({
		propertyBuildingOptions,
		propertyRoomOptions,
		selectedBuilding,
		selectedRoom,
		setSelectedBuilding,
		setSelectedRoom,
		setValue
	});

	useEffect(() => {
		if (!isRegisterAsBuilding && shouldDisableRegisterAsBuilding) {
			onChangeRegisterAsBuildingCheck(true);
			notify({
				content:
					"部屋の登録がない物件のため、「棟または戸建てとして登録」にチェックを入れました。",
				variant: "success"
			});
		}
	}, [
		isRegisterAsBuilding,
		shouldDisableRegisterAsBuilding,
		onChangeRegisterAsBuildingCheck,
		notify
	]);

	return (
		<>
			<FormRow error={errors.roomName?.message} label="物件名" required>
				<Grid alignItems="center" gap=".5rem" templateColumns="1fr 11rem">
					<SingleComboBox
						isInvalid={!!errors.roomName}
						loading={isPropertyBuildingOptionsValidating}
						onBlur={onBlurBuilding}
						onChange={onChangeBuilding}
						onInputChange={handleInputChangeBuilding}
						options={buildingOptions(propertyBuildingOptions)}
						placeholder="例）サンプルマンション"
						value={propertyBuildingOptionValue(selectedBuilding)}
						withAsyncOptions
					/>
					{selectedBuilding?.amaterasPropertyId ? (
						<TextLink
							color="link"
							href={
								landExclusiveDetailUrl ??
								editRentRoomBuildingUrl(selectedBuilding.amaterasPropertyId)
							}
							target="_blank"
						>
							物件詳細
						</TextLink>
					) : null}
				</Grid>
				<FormControlText help="2文字以上入力すると候補が最大100件まで表示されます。" />
				<FormControlText help="「・」などの記号を含む物件名がヒットしない場合は、記号を省いたフリガナで検索をお試しください。" />
			</FormRow>
			<FormRow error={errors.roomNumber?.message} label="部屋番号" required>
				<Flex direction="column" gap=".5rem">
					<InputCheckBox
						checked={isRegisterAsBuilding}
						disabled={shouldDisableRegisterAsBuilding}
						label="棟または戸建てとして登録"
						onChange={(event): void => {
							const { checked } = event.target;
							onChangeRegisterAsBuildingCheck(checked);
						}}
					/>
					<Grid alignItems="center" gap=".5rem" templateColumns="1fr 11rem">
						<SingleComboBox
							disabled={isRegisterAsBuilding}
							isInvalid={!!errors.roomNumber}
							loading={isPropertyRoomOptionsValidating}
							onBlur={onBlurRoom}
							onChange={onChangeRoom}
							onInputChange={handleInputChangeRoom}
							options={roomOptions(propertyRoomOptions)}
							placeholder="例）101"
							value={propertyRoomOptionValue(selectedRoom)}
							withAsyncOptions
						/>
						{selectedRoom?.amaterasPropertyId &&
						!selectedBuilding?.isLandExclusiveRoom ? (
							<TextLink
								color="link"
								href={
									showParkingSpaceProductLink
										? editRentParkingSpaceUrl(selectedRoom.amaterasPropertyId)
										: editRentRoomUrl(selectedRoom.amaterasPropertyId)
								}
								target="_blank"
							>
								物件詳細
							</TextLink>
						) : null}
					</Grid>
				</Flex>
			</FormRow>

			<FormRow error={errors.address?.message} label="物件住所" required>
				<Input
					disabled={shouldDisableAddress}
					isInvalid={!!errors.address}
					placeholder="例）東京都港区六本木1-2-3"
					{...register("address")}
				/>
			</FormRow>
		</>
	);
}
